import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    makeStyles,
    Paper,
    TextField,
    Typography
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { Add, Clear, Delete, Remove, Search } from "@material-ui/icons";
import { deleteInventoryItem, updateInventoryItemQty } from "../../redux/reducers/inventory/inventoryReducer";

const useStyles = makeStyles((theme) => ({
    searchInventoryInput: {
        "& .MuiInputBase-input": {
            fontSize: "16px",
            padding: "12px",
        },
        "& .MuiInputAdornment-positionStart": {
            margin: "0",
            width: "30px",
        },
        "& .MuiInputAdornment-positionEnd": {
            marginLeft: "-14px",
        },
        "& ::placeholder": {
            color: "#A0A3A6",
            fontSize: "15px",
            opacity: 1,
        },
    },
    searchIconButton: {
        color: "#A0A3A6",
    },
    searchDropdown: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: 10,
        opacity: 1,
        maxHeight: 250,
        overflowY: "auto",
        borderRadius: 4,
        scrollbarWidth: "thin",
        [theme.breakpoints.down("sm")]: {
            right: "-85px",
        },
    },
    searchDropdownItem: {
        border: "1px solid #BDBDBD",
        borderRadius: "8px",
        marginBottom: 6,
    },
    iconBox: {
        display: "flex",
        alignItems: "center",
        gap: 10,
    },
    iconColor: {
        color: "#000000DE",
    },
}));

const SearchInventory = ({ form }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [filteredItems, setFilteredItems] = useState([]);

    const inventoryData = useSelector((state) => state.inventoryInfo);

    useEffect(() => {
        if (form.values.search_inventory) {
            const searchText = form.values.search_inventory.toLowerCase();
            const results = [];
            inventoryData.get("inventory").forEach((mainCategory, mainIndex) => {
                mainCategory.get("category").forEach((subCategory, subIndex) => {
                    const subCategoryName = subCategory.get("displayName").toLowerCase();
                    if (subCategoryName.includes(searchText)) {
                        subCategory.get("items").forEach((item, itemIndex) => {
                            results.push({
                                item,
                                mainIndex,
                                subIndex,
                                itemIndex,
                                mainCategoryId: mainCategory.get("id"),
                                subCategoryId: subCategory.get("id"),
                            });
                        });
                    } else {
                        subCategory.get("items").forEach((item, itemIndex) => {
                            const itemName = item.get("displayName").toLowerCase();
                            if (itemName.includes(searchText)) {
                                results.push({
                                    item,
                                    mainIndex,
                                    subIndex,
                                    itemIndex,
                                    mainCategoryId: mainCategory.get("id"),
                                    subCategoryId: subCategory.get("id"),
                                });
                            }
                        });
                    }
                });
            });
            setFilteredItems(results);
        } else {
            setFilteredItems([]);
        }
    }, [inventoryData, form.values.search_inventory]);

    const updateQtyFn = (operation, selectedIndex) => {
        const params = {
            operation: operation,
            path: [
                "inventory",
                selectedIndex.mainIndex,
                "category",
                selectedIndex.subIndex,
                "items",
                selectedIndex.itemIndex,
            ],
        };
        dispatch(updateInventoryItemQty(params));
    };

    const deleteQtyFn = (mainCategoryId, subCategoryId, itemId) => {
        const params = {
            itemId: itemId,
            pathKeyId: {
                mainCategoryId: mainCategoryId,
                subCategoryId: subCategoryId,
            },
        };
        dispatch(deleteInventoryItem(params));
    };

    return (
        <Box className="position-relative">
            <TextField
                className={classes.searchInventoryInput}
                variant="outlined"
                color="primary"
                margin="dense"
                size="small"
                fullWidth
                placeholder="Search Inventory"
                name="search_inventory"
                onBlur={form.handleBlur}
                onChange={form.handleChange}
                value={form.values.search_inventory}
                helperText={form.touched.form && form.errors.form}
                error={Boolean(form.touched.form && form.errors.form)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <IconButton type="button" className={classes.searchIconButton}>
                                <Search />
                            </IconButton>
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            {form.values.search_inventory && (
                                <IconButton
                                    type="button"
                                    onClick={() => form.setFieldValue("search_inventory", "")}
                                >
                                    <Clear />
                                </IconButton>
                            )}
                        </InputAdornment>
                    ),
                }}
            />

            {form.values.search_inventory && (
                <Paper className={classes.searchDropdown} elevation={8}>
                    {filteredItems.length > 0 ? (
                        <List>
                            {filteredItems.map(({ item, mainIndex, subIndex, itemIndex, mainCategoryId, subCategoryId }) => (
                                <ListItem key={item.get("id")} className={classes.searchDropdownItem}>
                                    <ListItemText
                                        style={{ flex: 1 }}
                                        primary={
                                            <Typography style={{ fontWeight: 600 }}>
                                                {item.get("displayName")}
                                            </Typography>
                                        }
                                    />
                                    {item.get("qty") > 0 ? (
                                        <Box className={classes.iconBox}>
                                            {item.get("qty") === 1 ? (
                                                <IconButton
                                                    title="Delete Item"
                                                    size="small"
                                                    onClick={() => deleteQtyFn(mainCategoryId, subCategoryId, item.get("id"))}
                                                >
                                                    <Delete className={classes.iconColor} />
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    title="Remove Item"
                                                    size="small"
                                                    onClick={() => updateQtyFn("decrement", { mainIndex, subIndex, itemIndex })}
                                                >
                                                    <Remove className={classes.iconColor} />
                                                </IconButton>
                                            )}
                                            <Typography style={{ fontWeight: 600 }}>
                                                {item.get("qty")}
                                            </Typography>
                                            <IconButton
                                                title="Add Item"
                                                size="small"
                                                onClick={() => updateQtyFn("increment", { mainIndex, subIndex, itemIndex })}
                                            >
                                                <Add className={classes.iconColor} />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={() => updateQtyFn("increment", { mainIndex, subIndex, itemIndex })}
                                        >
                                            Add
                                        </Button>
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Box p={2}>
                            <Typography color="textSecondary" align="center">
                                No items found
                            </Typography>
                        </Box>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default SearchInventory;